/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/schedule.ts
 * PURPOSE: Business logic service layer for the Schedule API.
 *          2026-09-02: + getMyScheduleDraftStatus (#704 BOT-015).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + Issue #704 (BOT-015) fix
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: OR-Tools integration with <60s schedule generation
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 * - Related: backend/app/api/v1/schedule.py, frontend/src/hooks/useEmployeeDashboard.ts
 *  */

import { apiRequest } from "./config";

// ============================================================================
// SCHEDULE API TYPES - Aligned with backend/app/schemas/schedule.py
// Reference: docs/02-design/03-api-specifications/contracts/schedules.yaml
// ============================================================================

/**
 * Schedule status enum - matches backend ScheduleStatus
 */
export type ScheduleStatus =
  | "draft"
  | "published"
  | "confirmed"
  | "in_progress"
  | "completed"
  | "cancelled";

/**
 * Shift type enum - matches backend ShiftType
 */
export type ShiftType =
  | "morning"
  | "afternoon"
  | "evening"
  | "night"
  | "full_day"
  | "flexible"
  | "split";

/**
 * Schedule response from API - aligned with backend ScheduleResponse
 * DD-0050 v2.4 §8.2: canonical organization_id anchor (renamed from branch_id)
 */
export interface Schedule {
  id: string;
  user_id: string;
  organization_id: string;  // DD-0050 v2.4 §8.2 — was branch_id
  shift_id?: string;
  date: string;           // Format: YYYY-MM-DD
  start_time: string;     // Format: HH:MM:SS (aligned with backend spec)
  end_time: string;       // Format: HH:MM:SS (aligned with backend spec)
  break_duration: number; // minutes
  status: ScheduleStatus;
  position?: string;
  department_id?: string;
  is_recurring: boolean;
  is_flexible: boolean;
  is_open_for_swap: boolean;
  notes?: string;
  ai_suggested: boolean;
  ai_score?: number;
  created_at: string;
  updated_at?: string;
}

/**
 * Schedule create request - aligned with backend ScheduleCreate
 * DD-0050 v2.4 §8.2: canonical organization_id anchor (renamed from branch_id)
 */
export interface ScheduleCreate {
  user_id: string;
  organization_id: string;  // DD-0050 v2.4 §8.2 — was branch_id
  date: string;           // Format: YYYY-MM-DD
  start_time: string;     // Format: HH:MM:SS (aligned with backend spec)
  end_time: string;       // Format: HH:MM:SS (aligned with backend spec)
  shift_id?: string;
  break_duration?: number;
  position?: string;
  department_id?: string;
  notes?: string;
  is_recurring?: boolean;
  recurrence_pattern?: {
    type: "daily" | "weekly" | "monthly";
    days?: number[];
    interval?: number;
  };
  recurrence_end_date?: string;
}

/**
 * Schedule list response - for paginated lists
 */
export interface ScheduleListResponse {
  schedules: Schedule[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Shift template response - aligned with backend ShiftResponse
 */
export interface ShiftTemplate {
  id: string;
  name: string;
  code: string;
  branch_id: string;
  shift_type: ShiftType;
  start_time: string;
  end_time: string;
  break_duration: number;
  min_staff: number;
  max_staff?: number;
  required_skills?: string[];
  color_code?: string;
  is_active: boolean;
  created_at: string;
}

/**
 * Conflict check response
 */
export interface ConflictCheckResponse {
  has_conflicts: boolean;
  conflicts: Array<{
    user_id: string;
    date: string;
    existing_schedule: Schedule;
    conflict_type: "overlap" | "double_booking" | "rest_period_violation";
  }>;
}

export interface ShiftSwapRequest {
  schedule_id: string;
  target_user_id: string;
  reason: string;
}

/**
 * #704 (BOT-015): GET /schedules/my-schedule/draft-status response.
 * has_draft_schedule = true khi NV có ca status=DRAFT (Manager đang soạn,
 * chưa xuất bản) trong khoảng ngày — KHÔNG kèm chi tiết ca (ngày/giờ/chi
 * nhánh) để giữ nguyên nguyên tắc /my-schedule ẩn DRAFT khỏi Employee.
 */
export interface MyScheduleDraftStatusResponse {
  has_draft_schedule: boolean;
}

export const scheduleApi = {
  /**
   * Get personal schedule
   */
  async getMySchedule(token: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    
    return apiRequest<Schedule[]>(`/schedules/my-schedule?${params}`, {
      method: "GET",
      token,
    });
  },

  /**
   * #704 (BOT-015): kiểm tra NV có ca DRAFT (chưa xuất bản) trong khoảng
   * ngày — dùng để phân biệt "chưa có ca" thật với "có ca, Manager chưa
   * xuất bản" (getMySchedule ở trên cố ý ẩn DRAFT nên trả [] cho cả 2
   * trường hợp). Endpoint riêng, nhẹ — chỉ trả 1 boolean, không lộ chi
   * tiết ca DRAFT.
   */
  async getMyScheduleDraftStatus(token: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    return apiRequest<MyScheduleDraftStatusResponse>(
      `/schedules/my-schedule/draft-status?${params}`,
      { method: "GET", token },
    );
  },

  /**
   * Get all schedules (manager)
   */
  async getAllSchedules(token: string, filters?: any) {
    const params = new URLSearchParams(filters);
    return apiRequest<Schedule[]>(`/schedules?${params}`, {
      method: "GET",
      token,
    });
  },

  /**
   * Create new schedule
   */
  async createSchedule(token: string, data: ScheduleCreate) {
    return apiRequest<Schedule>("/schedules", {
      method: "POST",
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Bulk create schedules
   */
  async bulkCreateSchedules(token: string, schedules: ScheduleCreate[]) {
    return apiRequest<Schedule[]>("/schedules/bulk", {
      method: "POST",
      body: JSON.stringify({ schedules }),
      token,
    });
  },

  /**
   * Update schedule
   */
  async updateSchedule(token: string, id: string, data: Partial<Schedule>) {
    return apiRequest<Schedule>(`/schedules/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Publish draft schedule
   */
  async publishSchedule(token: string, id: string) {
    return apiRequest<Schedule>(`/schedules/${id}/publish`, {
      method: "POST",
      token,
    });
  },

  /**
   * Request shift swap
   */
  async requestSwap(token: string, data: ShiftSwapRequest) {
    return apiRequest("/schedules/swap", {
      method: "POST",
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Check schedule conflicts
   */
  async checkConflicts(token: string, userId: string, date: string) {
    return apiRequest(`/schedules/check-conflicts`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, date }),
      token,
    });
  },

  /**
   * Get shift templates
   */
  async getShiftTemplates(token: string) {
    return apiRequest("/schedules/shifts", {
      method: "GET",
      token,
    });
  },

  /**
   * Optimize schedule (AI)
   */
  async optimizeSchedule(token: string, date: string) {
    return apiRequest("/schedules/optimize", {
      method: "POST",
      body: JSON.stringify({ date }),
      token,
    });
  },

  /**
   * Get approval history for a schedule
   * Sprint 20 TD-PILOT-003: Wire real API for approval history
   */
  async getApprovalHistory(token: string, scheduleId: string, organizationId?: number) {
    const params = new URLSearchParams();
    if (organizationId) params.append("organization_id", organizationId.toString());

    return apiRequest<ApprovalHistoryEntry[]>(
      `/schedules/${scheduleId}/approval-history?${params}`,
      {
        method: "GET",
        token,
      }
    );
  },

  /**
   * Submit schedule for approval
   * Sprint 20 TD-PILOT-003: New endpoint for approval workflow
   */
  async submitForApproval(token: string, scheduleId: string, comment?: string) {
    return apiRequest(`/schedules/${scheduleId}/submit-for-approval`, {
      method: "POST",
      body: JSON.stringify({ comment }),
      token,
    });
  },

  /**
   * Approve or reject schedule
   * Sprint 20 TD-PILOT-003: New endpoint for approval workflow
   */
  async processApproval(
    token: string,
    scheduleId: string,
    action: "approve" | "reject",
    comment?: string
  ) {
    return apiRequest(`/schedules/${scheduleId}/process-approval`, {
      method: "POST",
      body: JSON.stringify({ action, comment }),
      token,
    });
  },
};

/**
 * Approval history entry type
 * Sprint 20 TD-PILOT-003: Type definition for approval workflow
 */
export interface ApprovalHistoryEntry {
  id: string;
  schedule_id: string;
  action: "SUBMITTED" | "APPROVED" | "REJECTED" | "REVISED" | "AUTO_APPROVED";
  user_id: string;
  user_name: string;
  user_role: string;
  comment?: string;
  timestamp: string;
  metadata?: {
    previous_status?: string;
    new_status?: string;
    changes_count?: number;
  };
}